<template>
  <v-dialog
    :model-value="modelValue"
    max-width="620px"
    persistent
    scrollable
    data-testid="tax-question-version-v2-dialog"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card @keydown.esc="close">
      <v-card-title class="bg-primary text-white d-flex align-center pa-4">
        <v-icon class="mr-2">{{ titleIcon }}</v-icon>
        <div class="flex-grow-1">
          <div class="text-h6">{{ titleText }}</div>
        </div>
        <v-btn icon variant="text" color="white" data-testid="qversion-v2-close-btn" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-6">
        <v-alert
          v-if="isCloneMode && initialData"
          type="info"
          variant="tonal"
          density="comfortable"
          class="mb-4"
          data-testid="qversion-v2-clone-hint"
        >
          โคลนจากชุดคำถาม <strong>{{ initialData.versionCode }}</strong> —
          คัดลอก Section และคำถามทั้งหมดไปเป็นชุดใหม่ (ชุดใหม่เป็น draft ยังไม่ active)
        </v-alert>

        <v-form ref="form">
          <v-text-field
            data-testid="qversion-v2-code-input"
            v-model="form.versionCode"
            label="Version code *"
            variant="outlined"
            density="comfortable"
            prepend-inner-icon="mdi-tag-outline"
            placeholder="Q1"
            :readonly="isEditMode"
            :hint="isEditMode ? 'Version code แก้ไขไม่ได้หลังสร้างแล้ว' : ''"
            :persistent-hint="isEditMode"
            :rules="isEditMode ? [] : [rules.required]"
            @update:model-value="form.versionCode = normalizeCode(form.versionCode)"
          />

          <v-text-field
            data-testid="qversion-v2-description-input"
            v-model="form.description"
            label="Description"
            variant="outlined"
            density="comfortable"
            prepend-inner-icon="mdi-text-box-outline"
            placeholder="รายละเอียดชุดคำถามนี้"
            class="mt-2"
          />

          <v-textarea
            v-if="!isCloneMode"
            data-testid="qversion-v2-resume-input"
            v-model="form.resumePromptTh"
            label="Resume prompt (ข้อความต้อนรับกลับ)"
            variant="outlined"
            density="comfortable"
            prepend-inner-icon="mdi-message-reply-text-outline"
            rows="3"
            auto-grow
            hint="ข้อความที่บอตใช้เมื่อผู้ใช้กลับมาทำแบบสอบถามต่อ"
            persistent-hint
            class="mt-2"
          />
        </v-form>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn data-testid="qversion-v2-cancel-btn" variant="outlined" :disabled="saving" @click="close">
          <v-icon start>mdi-close</v-icon>
          ยกเลิก
        </v-btn>
        <v-btn
          data-testid="qversion-v2-submit-btn"
          color="primary"
          variant="elevated"
          :loading="saving"
          @click="submit"
        >
          <v-icon start>{{ isCloneMode ? 'mdi-content-copy' : 'mdi-check' }}</v-icon>
          {{ submitText }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
/**
 * Dual-mode (edit | clone) dialog for a Tax Question V2 VERSION (the question
 * set itself, not its items). Mirrors `components/TaxConfigV2Dialog.vue` for
 * props/emit conventions: `modelValue` + `mode` + `initialData`, emits
 * `update:modelValue` and a single `submit` carrying the raw form payload — the
 * parent owns the API call.
 *
 * Field rules follow the backend contract:
 *  - UpdateTaxQuestionVersionV2Request = { description, resumePromptTh, isActive }
 *    → in EDIT mode `versionCode` is read-only (it cannot be changed) and the
 *      current `isActive` is passed through so a metadata save never flips it.
 *  - CloneTaxQuestionVersionV2Request  = { versionCode, description }
 *    → in CLONE mode `versionCode` is required and `resumePromptTh` is hidden
 *      (the clone endpoint does not accept it).
 */
export default {
  name: 'TaxQuestionVersionV2Dialog',
  props: {
    modelValue: { type: Boolean, default: false },
    /** 'edit' | 'clone' */
    mode: { type: String, default: 'edit' },
    /** the question version DTO being edited / cloned from */
    initialData: { type: Object, default: null },
    /** pre-filled code for clone mode (parent suggests the next free Q-code) */
    suggestedCode: { type: String, default: '' },
    /** parent-driven submit spinner */
    saving: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'submit'],
  data() {
    return {
      form: this.buildForm(),
      rules: {
        required: (v) => (v !== null && v !== undefined && String(v).trim() !== '') || 'Required',
      },
    };
  },
  computed: {
    isCloneMode() {
      return this.mode === 'clone';
    },
    isEditMode() {
      return this.mode !== 'clone';
    },
    titleIcon() {
      return this.isCloneMode ? 'mdi-content-copy' : 'mdi-pencil';
    },
    titleText() {
      return this.isCloneMode ? 'โคลนชุดคำถาม (Question Set V2)' : 'แก้ข้อมูลชุดคำถาม (Question Set V2)';
    },
    submitText() {
      return this.isCloneMode ? 'โคลน' : 'บันทึก';
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.resetForm();
    },
  },
  methods: {
    normalizeCode(value) {
      return String(value || '').replace(/\s+/g, '').toUpperCase();
    },
    buildForm() {
      return {
        questionVersionID: null,
        versionCode: '',
        description: '',
        resumePromptTh: '',
        isActive: false,
      };
    },
    resetForm() {
      const base = this.buildForm();
      const d = this.initialData;
      if (d) {
        base.questionVersionID = d.questionVersionID ?? null;
        base.isActive = Boolean(d.isActive);
        if (this.isCloneMode) {
          base.versionCode = this.normalizeCode(this.suggestedCode);
          base.description = d.versionCode ? `clone of ${d.versionCode}` : '';
        } else {
          base.versionCode = d.versionCode || '';
          base.description = d.description || '';
          base.resumePromptTh = d.resumePromptTh || '';
        }
      } else if (this.isCloneMode) {
        base.versionCode = this.normalizeCode(this.suggestedCode);
      }
      this.form = base;
      this.$nextTick(() => this.$refs.form?.resetValidation?.());
    },
    close() {
      this.$emit('update:modelValue', false);
    },
    async submit() {
      const res = await this.$refs.form?.validate?.();
      if (res && res.valid === false) return;
      const code = this.normalizeCode(this.form.versionCode);
      if (this.isCloneMode) {
        if (!code) return;
        this.$emit('submit', {
          mode: 'clone',
          questionVersionID: this.form.questionVersionID,
          versionCode: code,
          description: this.form.description || '',
        });
        return;
      }
      this.$emit('submit', {
        mode: 'edit',
        questionVersionID: this.form.questionVersionID,
        versionCode: code,
        description: this.form.description || '',
        resumePromptTh: this.form.resumePromptTh || '',
        isActive: Boolean(this.form.isActive),
      });
    },
  },
};
</script>
